/**
 * Unified ContentService — source adapters.
 *
 * Each adapter turns a resolved candidate URL into a final `ContentHandle`.
 * Adapters are pure functions of (ref, cfg, nsConfig) so they are trivially
 * unit-testable; the `ContentResolver` wires them together per policy.
 *
 *   LocalDiskAdapter    → dev machine disk (I:/mgv-assets) served by setupProxy
 *   StaticAdapter       → same-origin /data/... static assets
 *   CloudFrontSignedAdapter → backend sign endpoint → signed CloudFront URL
 *   S3StreamAdapter     → /api/{ns}/media/stream?key= (302 redirect to S3/CF)
 *   BackendProxyAdapter → /api/.../asset & radio HLS rewrite proxies
 */

import { getContentConfig } from './config.js';
import { getNamespaceConfig } from './namespaceConfig.js';
import { apiFetch } from '../utils/apiFetchPolicy';

function handle(url, source, extra = {}) {
  return {
    url,
    source,
    isStream: Boolean(extra.isStream),
    headers: extra.headers || undefined,
    expiresAt: extra.expiresAt,
    ttl: extra.ttl ?? 0,
  };
}

/** Source A — dev local disk. Only valid when localDisk is enabled. */
export function localDiskAdapter(ref, cfg, ns) {
  if (!cfg.localDisk) return null;
  const url = ns.buildDiskUrl ? ns.buildDiskUrl(ref, cfg) : null;
  if (!url) return null;
  return handle(url, 'disk');
}

/** Source B — same-origin static (baked CDN assets, unsigned). */
export function staticAdapter(ref, cfg, ns) {
  const url = ns.buildStaticUrl ? ns.buildStaticUrl(ref, cfg) : null;
  if (!url) return null;
  return handle(url, 'static');
}

/**
 * Source D — CloudFront signed. Calls the backend sign endpoint and returns the
 * resulting signed URL. `signFn` is injectable for tests; in the browser it
 * defaults to a fetch that resolves a `{ url }` JSON body. The returned handle
 * carries the signed URL's own `Expires` so the cache honors it.
 */
export async function cloudFrontSignedAdapter(ref, cfg, ns, { signFn } = {}) {
  const endpoint = ns.signEndpoint ? ns.signEndpoint(ref, cfg) : null;
  if (!endpoint) return null;
  let signedUrl;
  if (signFn) {
    signedUrl = await signFn(endpoint, ref);
  } else {
    signedUrl = await defaultSignFetch(endpoint);
  }
  if (!signedUrl) return null;
  return handle(signedUrl, 'cf-signed');
}

async function defaultSignFetch(endpoint) {
  try {
    const data = await apiFetch(endpoint, { credentials: 'include' });
    return data && (data.url || data.signedUrl) ? data.url || data.signedUrl : null;
  } catch {
    return null;
  }
}

/** Source C — S3 stream proxy (302 redirect to S3/CloudFront). */
export function s3StreamAdapter(ref, cfg, ns) {
  const url = ns.buildStreamUrl ? ns.buildStreamUrl(ref, cfg) : null;
  if (!url) return null;
  return handle(url, 's3-presigned', { isStream: true });
}

/** Source E — backend proxy (/api/.../asset, radio HLS). */
export function backendProxyAdapter(ref, cfg, ns) {
  const url = ns.buildBackendProxyUrl ? ns.buildBackendProxyUrl(ref, cfg) : null;
  if (!url) return null;
  return handle(url, 'backend-proxy', { isStream: true });
}

/** Re-export for callers that want the fetch default. */
export { defaultSignFetch };
